/**
 * @file AttachmentIdsUpload.tsx
 * @description 「附件 fileIds」受控上傳元件（antd Form 友好：value/onChange 為 number[]）。
 *
 * 用於新建案件申請明細（TOP 品項/範圍、TAP 明細行）的「附件上傳識別」：
 * 現階段 = 直傳 OSS 後把 files 表 ID 存進表單值（AI 識別自動填行留待後續）。
 *
 * 設計：
 * - 對外契約只有 number[]（files.id 陣列），落庫進 applicationDetails.attachmentFileIds；
 * - 內部維護 antd UploadFile 清單以顯示檔名 / 上傳中狀態；建立表單場景不需回顯歷史附件
 *   （value 由本元件自己累積），故不做「僅有 id 時反查檔名」。
 * - 上傳走 @/lib/file-upload 的 uploadFile（presign → PUT OSS → confirm），scope='case'；
 *   建案尚未有案件 id，故不帶 ownerType/ownerId（檔案先落庫，關聯由 applicationDetails 承載）。
 */

import { useRef, useState } from 'react';
import { Button, Upload } from 'antd';
import type { UploadFile, UploadProps } from 'antd';
import { Paperclip } from 'lucide-react';
import { message } from '@/lib/antd-app';
import { uploadFile } from '@/lib/file-upload';

interface AttachmentIdsUploadProps {
  /** 已上傳的 files 表 ID 陣列（antd Form 注入） */
  value?: number[];
  /** 表單值變更回調（antd Form 注入） */
  onChange?: (ids: number[]) => void;
  /** 附件數上限（與 shared schema attachmentFileIds max(50) 對齊，預設 10） */
  maxCount?: number;
  /** 按鈕文案 */
  buttonText?: string;
  disabled?: boolean;
}

/**
 * 附件上傳（存 fileIds）受控元件。
 * 多檔並行上傳時以 ref 持有最新 ids，避免閉包讀到過期的 value。
 */
export function AttachmentIdsUpload({
  value,
  onChange,
  maxCount = 10,
  buttonText = '附件',
  disabled,
}: AttachmentIdsUploadProps) {
  /** 顯示用的 antd 檔案清單（含上傳中 / 失敗狀態） */
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  /** 最新 ids 快照：多檔並行上傳完成順序不定，用 ref 做原子追加 */
  const idsRef = useRef<number[]>(value ?? []);
  idsRef.current = value ?? [];

  /** 自定義上傳：presign → PUT OSS → confirm，成功後把 file id 追加進表單值 */
  const customRequest: NonNullable<UploadProps['customRequest']> = (options) => {
    const file = options.file as File;
    const uid = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setFileList((prev) => [...prev, { uid, name: file.name, status: 'uploading' }]);
    void (async () => {
      try {
        const uploaded = await uploadFile(file, { scope: 'case' });
        setFileList((prev) =>
          prev.map((f) =>
            f.uid === uid ? { ...f, status: 'done' as const, response: uploaded } : f,
          ),
        );
        idsRef.current = [...idsRef.current, uploaded.id];
        onChange?.(idsRef.current);
      } catch (err) {
        // 業務必要：上傳失敗輸出錯誤日誌，便於排查（presign / OSS PUT / confirm 任一步）
        console.error('[AttachmentIdsUpload] upload failed', { filename: file.name, err });
        setFileList((prev) =>
          prev.map((f) => (f.uid === uid ? { ...f, status: 'error' as const } : f)),
        );
        message.error(`「${file.name}」上傳失敗，請重試`);
      }
    })();
  };

  /** 移除附件：從表單值剔除對應 file id（失敗項無 id，僅移除顯示） */
  const handleRemove: UploadProps['onRemove'] = (file) => {
    setFileList((prev) => prev.filter((f) => f.uid !== file.uid));
    const uploadedId = (file.response as { id?: number } | undefined)?.id;
    if (uploadedId) {
      idsRef.current = idsRef.current.filter((id) => id !== uploadedId);
      onChange?.(idsRef.current);
    }
    return true;
  };

  return (
    <Upload
      fileList={fileList}
      customRequest={customRequest}
      onRemove={handleRemove}
      maxCount={maxCount}
      multiple
      disabled={disabled}
    >
      <Button size="small" icon={<Paperclip size={12} />} disabled={disabled}>
        {buttonText}
      </Button>
    </Upload>
  );
}
